interface FormattingError {
  position: number;
  token: string;
  message: string;
}

/**
 * Checks a template string for formatting errors related to `{` and `}` usage.
 *
 * Rules:
 * - `{variable_name}` → allowed (variable placeholder)
 * - `{{` or `}}` → allowed (escaped brace)
 * - single `{` or `}` → error
 *
 * @param template The template string to validate.
 * @returns Array of error messages with position info.
 */
export const checkPromptFormattingErrors = (
  template: string
): FormattingError[] => {
  const errors: FormattingError[] = [];

  const regex = /{{|}}|{[^{}]+}|{|}/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    const token = match[0];
    const position = match.index;

    if (token === '{{' || token === '}}') continue;
    if (/^{[a-zA-Z0-9_]+}$/.test(token)) continue;

    // Error cases
    if (token === '{') {
      errors.push({
        position,
        token,
        message: `Unescaped "{" at position ${position}. Use "{{" instead.`,
      });
    } else if (token === '}') {
      errors.push({
        position,
        token,
        message: `Unescaped "}" at position ${position}. Use "}}" instead.`,
      });
    } else if (/^{[^{}]*}$/.test(token)) {
      errors.push({
        position,
        token,
        message: `Invalid variable format "${token}" at position ${position}. Use only letters, numbers, and underscores inside braces.`,
      });
    }
  }

  return errors;
};
